import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [activeBranch, setActiveBranchState] = useState(
    () => localStorage.getItem("sifcore_branch") || "all"
  );

  const loadBranches = useCallback(async () => {
    try {
      const { data } = await api.get("/branches");
      setBranches(data || []);
    } catch {
      setBranches([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await loadBranches();
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadBranches]);

  const setActiveBranch = (id) => {
    localStorage.setItem("sifcore_branch", id);
    setActiveBranchState(id);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("sifcore_token", data.token);
    setUser(data.user);
    await loadBranches();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("sifcore_token", data.token);
    setUser(data.user);
    await loadBranches();
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("sifcore_token");
    setUser(false);
  };

  const hasPermission = useCallback(
    (key) => {
      const perms = user?.permissions || [];
      return perms.includes("*") || perms.includes(key);
    },
    [user]
  );

  const forgotPassword = (email) => api.post("/auth/forgot-password", { email });
  const resetPassword = (token, new_password) => api.post("/auth/reset-password", { token, new_password });
  const changePassword = (current_password, new_password) =>
    api.post("/auth/change-password", { current_password, new_password });

  return (
    <AuthContext.Provider
      value={{
        user, loading, login, register, logout, forgotPassword, resetPassword, changePassword,
        hasPermission, branches, activeBranch, setActiveBranch, reloadBranches: loadBranches,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
